import { computed, inject, Injectable, signal } from '@angular/core';

import { WINDOW } from '@hglobal/common';
import { AuthService } from '@fb/services/auth/auth.service';

import { CTA_COOLDOWN_STORAGE_PREFIX, MS_PER_HOUR } from './cta.constants';
import { CtaDefinition } from './cta.models';

/** Map of CTA id to the epoch millisecond at which its cooldown expires. */
type CtaCooldownMap = Record<string, number>;

/**
 * Tracks per-user CTA cooldowns in localStorage so a triggered CTA stays hidden
 * until its cooldown elapses. The storage key is namespaced by a non-reversible
 * hash of the user's email (scoped per account, no email stored in clear text);
 * only CTA ids and expiry timestamps are persisted, validated and pruned on read.
 */
@Injectable({ providedIn: 'root' })
export class CtaCooldownService {
  readonly #auth = inject(AuthService);
  readonly #window = inject(WINDOW);

  readonly #cooldowns = signal<CtaCooldownMap>({});

  /** CTA ids whose cooldown is still active right now. Reactive. */
  readonly activeCooldownIds = computed<ReadonlySet<string>>(() => {
    const now = Date.now();
    const active = new Set<string>();

    for (const [ctaId, expiresAt] of Object.entries(this.#cooldowns())) {
      if (expiresAt > now) {
        active.add(ctaId);
      }
    }

    return active;
  });

  /** Storage namespace currently loaded into {@link #cooldowns}. */
  #loadedStorageKey: string | null = null;

  /**
   * Load the active user's cooldowns from storage, reloading when the user
   * changes. Call from an imperative path (component mount/navigation) — never
   * from inside a computed, since it writes a signal.
   */
  syncForCurrentUser(): void {
    const storageKey = this.#resolveStorageKey();

    if (storageKey === this.#loadedStorageKey) {
      return;
    }

    this.#loadedStorageKey = storageKey;
    this.#cooldowns.set(this.#read(storageKey));
  }

  /** Start a CTA's cooldown after it was triggered. No-op for zero-cooldown CTAs. */
  start(cta: CtaDefinition): void {
    if (cta.cooldownHours <= 0) {
      return;
    }

    this.syncForCurrentUser();

    const expiresAt = Date.now() + (cta.cooldownHours * MS_PER_HOUR);
    const next: CtaCooldownMap = {
      ...this.#pruneExpired(this.#cooldowns()),
      [cta.id]: expiresAt,
    };

    this.#cooldowns.set(next);
    this.#persist(next);
  }

  /**
   * Undo a CTA's cooldown — used when the action that triggered it ultimately
   * failed (e.g. the bot did not answer), so the suggestion should reappear.
   */
  cancel(ctaId: string): void {
    if (!(ctaId in this.#cooldowns())) {
      return;
    }

    const next = { ...this.#cooldowns() };
    delete next[ctaId];

    this.#cooldowns.set(next);
    this.#persist(next);
  }

  #resolveStorageKey(): string {
    return `${CTA_COOLDOWN_STORAGE_PREFIX}:${hashIdentifier(this.#auth.getUserEmail())}`;
  }

  #read(storageKey: string): CtaCooldownMap {
    const storage = this.#localStorage();

    if (!storage) {
      return {};
    }

    try {
      const raw = storage.getItem(storageKey);

      return raw ? this.#pruneExpired(parseCooldownMap(raw)) : {};
    } catch {
      return {};
    }
  }

  #persist(map: CtaCooldownMap): void {
    const storage = this.#localStorage();

    if (!storage || this.#loadedStorageKey === null) {
      return;
    }

    try {
      if (Object.keys(map).length === 0) {
        storage.removeItem(this.#loadedStorageKey);
      } else {
        storage.setItem(this.#loadedStorageKey, JSON.stringify(map));
      }
    } catch {
      /* ignore quota / disabled storage */
    }
  }

  #pruneExpired(map: CtaCooldownMap): CtaCooldownMap {
    const now = Date.now();
    const pruned: CtaCooldownMap = {};

    for (const [ctaId, expiresAt] of Object.entries(map)) {
      if (expiresAt > now) {
        pruned[ctaId] = expiresAt;
      }
    }

    return pruned;
  }

  #localStorage(): Storage | null {
    try {
      return this.#window.localStorage ?? null;
    } catch {
      return null;
    }
  }
}

/** Defensive parse: tolerate corrupted/tampered data, keep only id → finite number. */
function parseCooldownMap(raw: string): CtaCooldownMap {
  const parsed: unknown = JSON.parse(raw);

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }

  const result: CtaCooldownMap = {};

  for (const [ctaId, expiresAt] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof expiresAt === 'number' && Number.isFinite(expiresAt)) {
      result[ctaId] = expiresAt;
    }
  }

  return result;
}

/**
 * Non-cryptographic djb2 hash, used only to namespace storage per user without
 * persisting the raw email. Not a security boundary.
 */
function hashIdentifier(value: string): string {
  let hash = 5381;

  for (let index = 0; index < value.length; index++) {
    hash = ((hash << 5) + hash + value.charCodeAt(index)) | 0;
  }

  return (hash >>> 0).toString(36);
}
